'use client';

import React, { useState } from 'react';
import Link from 'next/link';

const profileData = {
  username: 'priya.creates',
  displayName: 'Priya Sharma',
  bio: '✨ Content Creator & Lifestyle Blogger\n📍 Mumbai, India\n🎥 New video every Tuesday!',
  location: 'Mumbai, India',
  avatar: 'PS',
  verified: true,
  followers: '340K',
  template: 'creator',
};

const socialLinks = [
  { id: 'social-instagram', icon: '📸', label: 'Instagram', href: '#', color: 'hover:text-pink-500' },
  { id: 'social-youtube', icon: '▶', label: 'YouTube', href: '#', color: 'hover:text-red-500' },
  { id: 'social-twitter', icon: '🐦', label: 'Twitter', href: '#', color: 'hover:text-sky-500' },
  { id: 'social-spotify', icon: '🎵', label: 'Spotify', href: '#', color: 'hover:text-green-500' },
];

const links = [
  {
    id: 'pub-link-001',
    title: '🎬 Watch My Latest YouTube Video',
    subtitle: 'How I built my first SaaS in 30 days',
    url: '#',
    clicks: 247,
    style: 'bg-gradient-to-r from-red-500 to-rose-600 text-white border-0',
    featured: true,
  },
  {
    id: 'pub-link-002',
    title: '📸 Follow Me on Instagram',
    subtitle: '@priya.creates · 340K followers',
    url: '#',
    clicks: 189,
    style: 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0',
    featured: false,
  },
  {
    id: 'pub-link-003',
    title: '🛍️ Shop My Merch Collection',
    subtitle: 'Limited edition drops every month',
    url: '#',
    clicks: 134,
    style: 'bg-white border border-border text-foreground hover:bg-muted',
    featured: false,
  },
  {
    id: 'pub-link-004',
    title: '💌 Join My Weekly Newsletter',
    subtitle: '12,000+ subscribers · Every Sunday',
    url: '#',
    clicks: 98,
    style: 'bg-white border border-border text-foreground hover:bg-muted',
    featured: false,
  },
  {
    id: 'pub-link-005',
    title: '🎙️ Listen to My Podcast',
    subtitle: 'The Creator Economy · Spotify',
    url: '#',
    clicks: 54,
    style: 'bg-white border border-border text-foreground hover:bg-muted',
    featured: false,
  },
  {
    id: 'pub-link-006',
    title: '📚 Free Creator Starter Kit',
    subtitle: 'Templates, tools & resources',
    url: '#',
    clicks: 31,
    style: 'bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0',
    featured: false,
  },
];

export default function PublicProfileContent() {
  const [clickedLinks, setClickedLinks] = useState<Set<string>>(new Set());

  const handleLinkClick = (id: string) => {
    // BACKEND INTEGRATION: POST /api/analytics/click with { linkId, profileId, timestamp }
    setClickedLinks((prev) => new Set([...prev, id]));
  };

  return (
    <div className="min-h-screen template-creator">
      {/* Top bar — only shown to owner */}
      <div className="bg-foreground/90 backdrop-blur-sm px-4 py-2.5 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success animate-pulse-soft" />
          <span className="text-white/80 text-xs font-500">Your page is live</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/dashboard"
            className="text-xs font-600 text-white/70 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-white/10"
          >
            ← Dashboard
          </Link>
          <Link
            href="/template-selection"
            className="text-xs font-600 bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary/80 transition-colors"
          >
            Edit Page
          </Link>
        </div>
      </div>

      {/* Profile content */}
      <div className="max-w-lg mx-auto px-4 py-10 pb-16">
        {/* Avatar & name */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-brand flex items-center justify-center text-white font-800 text-3xl border-4 border-white shadow-lg">
              {profileData.avatar}
            </div>
            {profileData.verified && (
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary border-2 border-white flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>

          <h1 className="text-2xl font-800 text-foreground mb-1">{profileData.displayName}</h1>
          <p className="text-sm font-500 text-muted-foreground mb-3">@{profileData.username}</p>

          <p className="text-sm text-foreground/80 leading-relaxed max-w-xs whitespace-pre-line mb-4">
            {profileData.bio}
          </p>

          {/* Social row */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.href}
                className={`w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center text-lg transition-all duration-200 hover:scale-110 hover:shadow-md ${social.color}`}
                title={social.label}
                aria-label={`Visit ${social.label}`}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Links */}
        <div className="space-y-3">
          {links.map((link) => (
            <a
              key={link.id}
              href={link.url}
              onClick={() => handleLinkClick(link.id)}
              className={`flex items-center gap-4 w-full px-5 py-4 rounded-2xl border transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${link.style} ${
                link.featured ? 'shadow-md' : ''
              }`}
            >
              <div className="flex-1 text-left">
                <p className={`font-700 text-sm ${link.style.includes('text-white') ? 'text-white' : 'text-foreground'}`}>
                  {link.title}
                </p>
                {link.subtitle && (
                  <p className={`text-xs mt-0.5 ${link.style.includes('text-white') ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {link.subtitle}
                  </p>
                )}
              </div>
              <svg
                className={`w-4 h-4 flex-shrink-0 ${link.style.includes('text-white') ? 'text-white/70' : 'text-muted-foreground'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 flex flex-col items-center gap-2">
          <Link
            href="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-border card-shadow hover:card-shadow-hover transition-all duration-200 hover:-translate-y-0.5"
          >
            <div className="w-5 h-5 rounded-md bg-gradient-brand flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <span className="text-xs font-700 text-foreground">Made with BioLink</span>
          </Link>
          <p className="text-xs text-muted-foreground">
            Create your free bio page at biolink.app
          </p>
        </div>
      </div>
    </div>
  );
}